import { useEffect, useState } from 'react';

import { useLayout } from '../../../../context/LayoutContext';
import {
  useAddPostCommentReaction,
  useDeletePostComment,
  usePostPutPostComment,
} from '../../../../services/redux/api/postsApi';
import { IComment } from '../../../../types/interfaces/comment';
import { INewFile } from '../../../../types/interfaces/new-file';
import { IAppFile } from '../../../../types/interfaces/app-file';
import { uploadFilesUtils } from '../../../../utils/upload-files-utils';
import { useGetMyDetails } from '../../../../services/redux/api/usersApi';
import { useFileList } from '../../../../hooks/use-file-list';

export function useComment(comment: IComment) {
  const { showLoader, hideLoader } = useLayout();
  const [addCommentReaction] = useAddPostCommentReaction();
  const [postPutPostComment] = usePostPutPostComment();
  const [deletePostComment] = useDeletePostComment();
  const { data: user } = useGetMyDetails();

  const {
    fileIdsToDelete,
    filesToUpload,
    resultFiles: commentFiles,
    onDeleteFile,
    onUploadFiles,
    resetFilesState,
  } = useFileList(comment.files);
  const [editedComment, setEditedComment] = useState<IComment>(null);
  const [userReaction, setUserReaction] = useState<boolean>(null);

  useEffect(() => {
    if (userReaction === null) {
      setUserReaction(
        !!comment.reactions?.find((reaction) => reaction.userId === user.id),
      );
    }
  }, [comment]);

  function onToggleToEditComment() {
    setEditedComment(comment);
  }

  function onCommentEdit(newValue: string) {
    setEditedComment({ ...comment, content: newValue });
  }

  function onCommentSave() {
    const loaderId = showLoader();

    return postPutPostComment({
      postId: comment.postId,
      id: comment.id,
      body: {
        comment: { ...comment, ...editedComment },
        fileIdsToDelete,
        filesToUpload,
      },
    })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        hideLoader(loaderId);
        setEditedComment(null);
        resetFilesState();
      });
  }

  function onCommentReaction() {
    if (!comment.postId) return;

    return addCommentReaction({ postId: comment.postId, id: comment.id })
      .then(() => setUserReaction((prev) => !prev))
      .catch((e) => console.log(e));
  }

  function onCommentRemove() {
    if (!comment.postId) return;

    const loaderId = showLoader();

    return deletePostComment({ postId: comment.postId, id: comment.id })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        hideLoader(loaderId);
      });
  }

  return {
    onCommentRemove,
    onCommentSave,
    onCommentReaction,
    onCommentEdit,
    onToggleToEditComment,
    editedComment,
    commentFiles,
    onDeleteFile,
    onUploadFiles,
    userReaction,
    user,
  };
}
